import React, { useCallback, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues$key } from '@atlassian/jira-relay/src/__generated__/exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues.graphql';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';
import type { AsyncExportItem, ExportIssuesDownloadError } from '../../../../common/types.tsx';
import { DownloadFlag } from '../../../../common/ui/download-flag/index.tsx';
import { ExportErrorFlag } from '../../../../common/ui/export-error-flag/index.tsx';
import messages from './messages.tsx';

type Props = {
	exportItem: AsyncExportItem;
	onComplete: () => void;
	onDismissed: () => void;
	exportIssuesTask: exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues$key;
};

export const ExportIssuesTaskDownloadFlag = ({
	exportItem,
	exportIssuesTask,
	onComplete,
	onDismissed,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [error, setError] = useState<ExportIssuesDownloadError | undefined>();

	const exportIssuesTaskData =
		useFragment<exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues$key>(
			graphql`
				fragment exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues on JiraIssueExportTaskCompleted {
					task {
						id
					}
					downloadResultUrl
				}
			`,
			exportIssuesTask,
		);

	const handleOnError = useCallback(
		(downloadError: ExportIssuesDownloadError) => {
			setError(downloadError);

			fireErrorAnalytics({
				event: createAnalyticsEvent({}), // forces contextual attributes be included
				meta: {
					id: 'JiraIssueExportTaskDownloadFailed',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error: downloadError,
				attributes: {
					taskId: exportIssuesTaskData.task?.id,
					errorCode: downloadError?.errorCode,
				},
			});
		},
		[createAnalyticsEvent, exportIssuesTaskData.task?.id],
	);

	const handleOnDownloadDismissed = useCallback(() => {
		const cancelEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(cancelEvent, 'cancelDownloadJiraIssueExportTask', {
			taskId: exportIssuesTaskData?.task?.id,
		});
		onDismissed();
	}, [createAnalyticsEvent, exportIssuesTaskData?.task?.id, onDismissed]);

	const id = exportIssuesTaskData.task?.id || 'export-issues-download-flag';
	const downloadResultUrl = exportIssuesTaskData.downloadResultUrl || '';

	if (error) {
		return (
			<ExportErrorFlag
				id={`export-issues-task-flag-download-error-${exportItem.id}`}
				errorTitle={formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.exportIssuesFlagDownloadErrorTitleIssueTerminologyRefreshM4
						: messages.exportIssuesFlagDownloadErrorTitle,
				)}
				onDismissed={onDismissed}
			/>
		);
	}

	return (
		<DownloadFlag
			id={id}
			downloadUrl={downloadResultUrl}
			fileExtension={exportItem.fileExtension}
			onDismissed={handleOnDownloadDismissed}
			onComplete={onComplete}
			onError={handleOnError}
		/>
	);
};
